import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/app/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { useUser, useUpdateUser } from '@/hooks/use-settings';
import { useAuth } from '@/contexts/auth-context';
import { useBulkUpdateModuleRoles, useModuleRoles } from '@/hooks/use-admin';
import { usePermissions } from '@/hooks/use-permissions';
import { usePlatformModules } from '@/hooks/use-modules';
import { getModuleDisplayName, getPlatformModules } from '@/lib/modules';
import { handleApiError } from '@/lib/api-client';
import { CompanyRoleSelectItems } from '@/app/components/company-role-select-items';
import { ModuleRoleSyncDialog } from '@/app/components/module-role-sync-dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/app/components/ui/alert-dialog';
import { companyRoleToModuleRole } from '@/lib/module-roles';
import type { UserRole, EmploymentType, ModuleRoleLevel } from '@/lib/types';
import { toast } from 'sonner';

const MODULE_ROLE_OPTIONS: { value: ModuleRoleLevel; label: string; description: string }[] = [
  { value: 'no_access', label: 'No Access', description: 'Cannot access this module' },
  { value: 'viewer', label: 'Viewer', description: 'Read-only access' },
  { value: 'user', label: 'User', description: 'Can interact with documents' },
  { value: 'supervisor', label: 'Supervisor', description: 'Can create documents and run talks' },
  { value: 'inspector', label: 'Inspector', description: 'Can inspect and review records' },
  { value: 'manager', label: 'Manager', description: 'Full access except destructive actions' },
  { value: 'admin', label: 'Admin', description: 'Full access' },
];

export function EditEmployeePage({ basePath = '/people' }: { basePath?: string } = {}) {
  const { id } = useParams<{ id: string }>();
  const userId = Number(id);
  const { user: currentUser } = useAuth();
  const { canManageUsers } = usePermissions();
  const navigate = useNavigate();
  const updateMutation = useUpdateUser();
  const updateModuleRoles = useBulkUpdateModuleRoles();
  const {
    enabledModules,
    isLoading: modulesLoading,
    error: modulesError,
  } = usePlatformModules();
  const platformKeys = getPlatformModules().map(m => m.key);
  const topLevelModules = enabledModules.filter(mod => platformKeys.includes(mod));

  const { data: user, isLoading: userLoading, isError: userError } = useUser(userId);

  const { data: moduleRolesData, isLoading: moduleRolesLoading, isError: moduleRolesError } =
    useModuleRoles({ user: userId, page_size: 100 }, { enabled: canManageUsers });

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [employmentType, setEmploymentType] = useState<EmploymentType>('full_time');
  const [role, setRole] = useState<UserRole>('Viewer');
  const [moduleRoles, setModuleRoles] = useState<Record<string, ModuleRoleLevel>>({});
  const [originalModuleRoles, setOriginalModuleRoles] = useState<Record<string, ModuleRoleLevel>>({});
  const [adpFileNumber, setAdpFileNumber] = useState('');
  const [xeroEmployeeNumber, setXeroEmployeeNumber] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [roleSyncOpen, setRoleSyncOpen] = useState(false);
  const [adminPromotionOpen, setAdminPromotionOpen] = useState(false);
  const [pendingMfaWarning, setPendingMfaWarning] = useState<string | null>(null);

  // Seed profile fields whenever the fetched user changes
  useEffect(() => {
    if (!user) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setFirstName(user.first_name || '');
    setLastName(user.last_name || '');
    setPhone(user.phone || '');
    setJobTitle(user.job_title || '');
    setEmploymentType(user.employment_type || 'full_time');
    setRole(user.role || 'Viewer');
    setAdpFileNumber(user.adp_file_number || '');
    setXeroEmployeeNumber(user.xero_employee_number || '');
  }, [user]);

  // Seed module roles whenever they load
  useEffect(() => {
    if (!moduleRolesData?.results) return;
    const roles: Record<string, ModuleRoleLevel> = {};
    for (const mr of moduleRolesData.results) {
      roles[mr.module] = mr.role;
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setModuleRoles(roles);
    setOriginalModuleRoles(roles);
  }, [moduleRolesData]);

  const updateModuleRole = (moduleKey: string, roleValue: string) => {
    if (!roleValue) return;
    setModuleRoles((prev) => ({
      ...prev,
      [moduleKey]: roleValue as ModuleRoleLevel,
    }));
  };

  const getChangedModuleRoles = (): Record<string, ModuleRoleLevel> =>
    Object.fromEntries(
      topLevelModules
        .filter(
          (moduleKey) =>
            (moduleRoles[moduleKey] ?? 'no_access') !==
            (originalModuleRoles[moduleKey] ?? 'no_access'),
        )
        .map((moduleKey) => [moduleKey, moduleRoles[moduleKey] ?? 'no_access']),
    );

  const saveModuleRoles = async (roles: Record<string, ModuleRoleLevel>) => {
    if (Object.keys(roles).length === 0) return null;
    const response = await updateModuleRoles.mutateAsync({ user: userId, roles });
    return response.mfa_warning ?? null;
  };

  const saveEmployee = async (confirmAdminGrant = false) => {
    setError(null);
    setSaving(true);

    if (!firstName || !lastName) {
      setError('First name and last name are required.');
      setSaving(false);
      return;
    }
    if (canManageUsers && user?.role !== role && (moduleRolesError || modulesError)) {
      setError('Module access could not be loaded. Please try again.');
      setSaving(false);
      return;
    }

    try {
      // Update user fields via PATCH /api/v1/users/:id/
      const patchData: Record<string, unknown> = {
        first_name: firstName,
        last_name: lastName,
        phone,
        job_title: jobTitle,
        adp_file_number: adpFileNumber.trim(),
        xero_employee_number: xeroEmployeeNumber.trim(),
      };
      if (canManageUsers) {
        patchData.role = role;
      }
      if (confirmAdminGrant) {
        patchData.confirm_admin_grant = true;
      }
      if (user?.has_tech_profile) {
        patchData.employment_type = employmentType;
      }
      const response = await updateMutation.mutateAsync({
        id: userId,
        data: patchData,
      });

      if (canManageUsers && user?.role !== role) {
        setPendingMfaWarning(response.mfa_warning ?? null);
        setRoleSyncOpen(true);
        return;
      }

      let moduleWarning: string | null = null;
      if (canManageUsers) {
        moduleWarning = await saveModuleRoles(getChangedModuleRoles());
      }
      toast.success('Employee updated');
      [response.mfa_warning, moduleWarning]
        .filter((warning): warning is string => Boolean(warning))
        .filter((warning, index, allWarnings) => allWarnings.indexOf(warning) === index)
        .forEach((warning) => toast.warning(warning, { duration: 10000 }));
      navigate(basePath);
    } catch (err) {
      const apiError = handleApiError(err);
      if (typeof apiError === 'object' && 'message' in apiError) {
        setError(apiError.message);
      } else {
        setError(String(apiError));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!firstName || !lastName) {
      setError('First name and last name are required.');
      return;
    }
    if (canManageUsers && user?.role !== role && (moduleRolesError || modulesError)) {
      setError('Module access could not be loaded. Please try again.');
      return;
    }
    if (canManageUsers && role === 'Admin' && user?.role !== 'Admin') {
      setAdminPromotionOpen(true);
      return;
    }
    await saveEmployee();
  };

  const handleRoleSync = async (updateToNewRole: boolean) => {
    setError(null);
    setSaving(true);
    try {
      let moduleWarning: string | null = null;
      if (updateToNewRole) {
        moduleWarning = await saveModuleRoles(
          Object.fromEntries(
            topLevelModules.map((moduleKey) => [
              moduleKey,
              companyRoleToModuleRole(role),
            ]),
          ),
        );
      } else {
        // Keep the module access exactly as edited on the form.
        moduleWarning = await saveModuleRoles(getChangedModuleRoles());
      }
      setRoleSyncOpen(false);
      toast.success('Employee updated');
      [pendingMfaWarning, moduleWarning]
        .filter((warning): warning is string => Boolean(warning))
        .filter((warning, index, allWarnings) => allWarnings.indexOf(warning) === index)
        .forEach((warning) => toast.warning(warning, { duration: 10000 }));
      setPendingMfaWarning(null);
      navigate(basePath);
    } catch (err) {
      const apiError = handleApiError(err);
      setError(
        typeof apiError === 'object' && 'message' in apiError
          ? apiError.message
          : String(apiError),
      );
    } finally {
      setSaving(false);
    }
  };

  if (userLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (userError) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <div className="flex items-center gap-4">
          <Button asChild variant="ghost" size="icon">
            <Link to={basePath}>
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Unable to Load Employee</h1>
            <p className="text-gray-600 mt-1">You may not have permission to edit this employee, or the user does not exist.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <div className="flex items-center gap-4">
          <Button asChild variant="ghost" size="icon">
            <Link to={basePath}>
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">User Not Found</h1>
            <p className="text-gray-600 mt-1">The employee you are looking for does not exist.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center gap-4">
        <Button asChild variant="ghost" size="icon">
          <Link to={basePath}>
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Edit Employee</h1>
          <p className="text-gray-600 mt-1">
            Update details for {user.first_name} {user.last_name || user.username}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Personal Information */}
        <Card>
          <CardHeader>
            <CardTitle>Personal Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">First Name *</Label>
                <Input
                  id="first_name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="last_name">Last Name *</Label>
                <Input
                  id="last_name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payroll Integration */}
        <Card>
          <CardHeader>
            <CardTitle>Payroll Integration</CardTitle>
            <CardDescription>Optional identifiers used only when exporting payroll to ADP or Xero</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="adp_file_number">ADP File Number</Label>
                <Input
                  id="adp_file_number"
                  value={adpFileNumber}
                  onChange={(e) => setAdpFileNumber(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="xero_employee_number">Xero Employee Number</Label>
                <Input
                  id="xero_employee_number"
                  value={xeroEmployeeNumber}
                  onChange={(e) => setXeroEmployeeNumber(e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Employment Information */}
        <Card>
          <CardHeader>
            <CardTitle>Employment Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="job_title">Job Title</Label>
                <Input
                  id="job_title"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                />
              </div>
              {user.has_tech_profile && (
                <div>
                  <Label htmlFor="employment_type">Employment Type</Label>
                  <Select
                    value={employmentType}
                    onValueChange={(v) => setEmploymentType(v as EmploymentType)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full_time">Full Time</SelectItem>
                      <SelectItem value="part_time">Part Time</SelectItem>
                      <SelectItem value="contractor">Contractor</SelectItem>
                      <SelectItem value="subcontractor">Subcontractor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {canManageUsers && (
          <Card>
            <CardHeader>
              <CardTitle>Role & Permissions</CardTitle>
              <CardDescription>Set the organization role and per-module access levels</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="role">Organization Role</Label>
                <Select
                  value={role}
                  onValueChange={(v) => {
                    if (v) {
                      setRole(v as UserRole);
                    }
                  }}
                  disabled={currentUser?.id === userId}
                >
                  <SelectTrigger
                    id="role"
                    className="w-full sm:w-64"
                    title={
                      currentUser?.id === userId
                        ? 'You cannot change your own company role'
                        : undefined
                    }
                  >
                    <SelectValue>{role}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <CompanyRoleSelectItems />
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="mb-2 block">Module Access</Label>
                <div className="border rounded-lg divide-y">
                  {topLevelModules.map((mod) => (
                    <div key={mod} className="flex items-center justify-between px-4 py-3">
                      <span className="text-sm font-medium">{getModuleDisplayName(mod)}</span>
                      <Select
                        value={moduleRoles[mod] || 'no_access'}
                        onValueChange={(v) => updateModuleRole(mod, v)}
                      >
                        <SelectTrigger className="w-36">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MODULE_ROLE_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="flex gap-3">
          <Button
            type="submit"
            disabled={
              saving || (canManageUsers && (moduleRolesLoading || modulesLoading))
            }
          >
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save Changes
          </Button>
          <Button type="button" variant="outline" asChild>
            <Link to={basePath}>Cancel</Link>
          </Button>
        </div>
      </form>
      {canManageUsers && (
        <AlertDialog
          open={adminPromotionOpen}
          onOpenChange={setAdminPromotionOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Promote user to Admin?</AlertDialogTitle>
              <AlertDialogDescription>
                Admins have full access to company settings, users, and every enabled module.
                Promote {user.username} only if they should have that access.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                disabled={saving}
                onClick={() => {
                  setAdminPromotionOpen(false);
                  void saveEmployee(true);
                }}
              >
                {saving ? 'Saving...' : 'Promote to Admin'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
      {canManageUsers && (
        <ModuleRoleSyncDialog
          open={roleSyncOpen}
          userName={[firstName, lastName].filter(Boolean).join(' ') || user.username}
          newRole={role}
          modules={topLevelModules}
          currentRoles={Object.fromEntries(
            topLevelModules.map((moduleKey) => [
              moduleKey,
              moduleRoles[moduleKey] ?? 'no_access',
            ]),
          )}
          isUpdating={saving}
          onUpdate={() => handleRoleSync(true)}
          onKeep={() => handleRoleSync(false)}
        />
      )}
    </div>
  );
}
